import {
  MutableRefObject,
  useCallback, useMemo, useRef, useState,
} from 'react';
import {
  Box, Card, Typography, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar, Label } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode, getIsPrintMode } from '../../../../store/common/selectors';
import { useTable } from '../../../../hooks/useTable';
import { MemoizedFilterTabs } from '../../../../components/base';
import { getMembers } from '../../../../store/team/selectors';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { getCommissionTeams } from '../../../../store/commissions/selectors';
import { INDEX_TO_COLOR } from '../../../../data';
import { useTeamCommissions } from '../../../commissions/hooks/useTeamCommissions';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { filterOrdersWithLocation } from '../../../../utils/helpers/orders.helper';
import { OrdersCountLoader, OrdersTableHeader } from '../../Shared/components';
import { useCommonOrders } from '../../../../hooks/useCommonOrders';
import { TMember } from '../../../../types';

const TeamsSummaryTable = () => {
  const [filterStatus, setFilterStatus] = useState(0);
  const { t } = useTranslation(['orders', 'dashboard', 'common', 'inputs', 'commissions']);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const {
    localText, handleScrollTable, exporting, handleExport,
  } = useTable(apiRef);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const commissionTeams = useAppSelector(getCommissionTeams);
  const { pageHeight, resizableDiv } = useScreenResize(59);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const [isFullTable, setIsFullTable] = useState(false);

  const {
    setIsFetchByDate,
  } = useCommonOrders({
    prevTab: '2',
  });

  const {
    calculateTeamStepsCommission,
    calculateSplitCommission,
    calculateDuplicateCommission,
    aggregateTeamOrders,
    locationNamesById,
  } = useTeamCommissions();

  const { shop } = useAppSelector(getAuthenticateUser);

  const members = useAppSelector(getMembers);

  const teamNetSalesAndCommissionsById = useMemo(() => commissionTeams.reduce((acc, team) => {
    const teamOrders = team.commissionModel.compensation === 'team steps'
    && team.commissionModel.goalsBase === 'location' ? aggregateTeamOrders : aggregateTeamOrders?.filter((order) => order.teamId === team.id);
    const teamNetSales = teamOrders?.reduce((sum, order) => sum + order.summary.netSales, 0);

    let commissionData;

    switch (team.commissionModel?.compensation) {
      case 'team steps':
        commissionData = calculateTeamStepsCommission(team, teamOrders, teamNetSales);
        break;
      case 'split':
        commissionData = calculateSplitCommission(team, teamOrders, teamNetSales);
        break;
      case 'duplicate':
        commissionData = calculateDuplicateCommission(teamOrders, teamNetSales);
        break;
      default:
        commissionData = { netSales: teamNetSales, commission: 0, employeeCommissions: {} };
    }

    acc[team.id] = commissionData;

    return acc;
  }, {} as Record<number, {
    netSales: number;
    commission: number;
    employeeCommissions: { [key: string]: number } }>), [commissionTeams, aggregateTeamOrders]);

  const tableData = useMemo(() => {
    const dataByMemberAndLocation: Record<string, Record<number, number>> = {};
    const memberTotals: Record<string, number> = {};
    let dateNetSalesTotal = 0;
    const emailToMemberKey: Record<string, string> = {};

    const teamNetSalesTotal = Object.values(teamNetSalesAndCommissionsById).reduce((sum, data) => sum + data.netSales, 0);
    const totalCommission = Object.values(teamNetSalesAndCommissionsById).reduce((sum, data) => sum + data.commission, 0);

    members.forEach((member) => {
      let memberKey = member.shopifyId ? String(member.shopifyId) : undefined;

      if (!memberKey && Array.isArray(member.commissionTags) && member.commissionTags.length > 0) {
        memberKey = `tag-${member.commissionTags[0].toLowerCase()}`;
      }

      if (!memberKey && member.email) {
        memberKey = `email-${member.email.toLowerCase()}`;
      }

      if (memberKey && member.email) {
        emailToMemberKey[member.email.toLowerCase()] = memberKey;
      }

      if (memberKey) {
        dataByMemberAndLocation[memberKey] = {};
      }
    });

    aggregateTeamOrders.forEach((order) => {
      const memberId = String(order.userId);
      const { locationId } = order;
      let member = members.find((m) => String(m.shopifyId) === memberId);
      let memberKey: string | undefined = member?.shopifyId ? String(member.shopifyId) : undefined;

      if (member && memberKey) {
        emailToMemberKey[member.email.toLowerCase()] = memberKey;
      }

      if (!member) {
        member = members.find(
          (m) => Array.isArray(m.commissionTags)
            && Array.isArray(order.tags)
            && order.tags.some((tag) => m.commissionTags.some((commissionTag) => commissionTag.toLowerCase() === tag.toLowerCase())),
        );

        if (member) {
          memberKey = `tag-${member?.commissionTags?.[0]}`;
          emailToMemberKey[member.email.toLowerCase()] = memberKey;
        }
      }

      if (memberKey && locationId) {
        if (!dataByMemberAndLocation[memberKey]) {
          dataByMemberAndLocation[memberKey] = {};
        }

        if (!dataByMemberAndLocation[memberKey][locationId]) {
          dataByMemberAndLocation[memberKey][locationId] = 0;
        }

        dataByMemberAndLocation[memberKey][locationId] += order.summary.netSales;
      }
    });

    const rows = commissionTeams.flatMap((team) => team.members.flatMap((teamMember) => {
      const memberKey = emailToMemberKey[teamMember.email?.toLowerCase()];

      // 1. Get all orders for this member (regardless of location)
      const memberOrders = aggregateTeamOrders.filter(
        (order) => String(order.userId) === String(teamMember.shopifyId)
            || (Array.isArray(order.tags)
              && Array.isArray(teamMember.commissionTags)
              && order.tags.some((tag) => teamMember.commissionTags.some(
                (commissionTag) => commissionTag.toLowerCase() === tag.toLowerCase(),
              ))),
      );

      // 2. Compute member total and store
      const memberTotal = memberOrders.reduce((sum, order) => sum + order.summary.netSales, 0);
      memberTotals[memberKey] = memberTotal;

      // 3. Extract location map from precomputed data
      const locations = dataByMemberAndLocation[memberKey] || {};
      const sortedLocations = Object.entries(locations).sort(([a], [b]) => Number(a) - Number(b));

      // 4. Determine commission amount
      let memberCommission = 0;
      if (team.commissionModel?.compensation === 'team steps') {
        memberCommission = teamNetSalesAndCommissionsById[team.id]?.employeeCommissions[teamMember.email];
      } else if (team.commissionModel?.compensation === 'split') {
        memberCommission = teamNetSalesAndCommissionsById[team.id]?.employeeCommissions[teamMember.email] || 0;
      } else {
        memberCommission = teamNetSalesAndCommissionsById[team.id]?.commission || 0;
      }

      // 5. If member has no locations (or not matched to any), handle fallback
      if (Object.keys(locations)?.length === 0) {
        const unassignedOrders = memberOrders.filter(
          (order) => !order.locationId || !locationNamesById[order.locationId],
        );

        if (unassignedOrders.length === 0) {
          // No orders at all
          return {
            id: `${teamMember.email}-${team.teamName}-no-orders`,
            name: teamMember.name,
            email: teamMember.email,
            locationId: null,
            locationName: 'Unassigned',
            dateNetSales: 0,
            color: 'transparent',
            dates: '',
            memberTotal,
            locationTotal: 0,
            teamId: team.id,
            commissionModel: `${team.commissionModel?.compensation}-${team.id}`,
            teamName: team.teamName,
            teamNetSales: teamNetSalesAndCommissionsById[team.id]?.netSales || 0,
            teamCommission: memberCommission || 0,
            totalCommission: team.commissionModel?.compensation === 'team steps'
              ? 0
              : teamNetSalesAndCommissionsById[team.id]?.commission,
          };
        }

        const dates = Array.from(
          new Set(
            unassignedOrders.map((order) => moment.tz(order.processedAt, shop?.shopTimezone).format('YYYY-MM-DD')),
          ),
        );

        return dates.map((date) => {
          const dateNetSales = unassignedOrders
            .filter(
              (order) => moment.tz(order.processedAt, shop?.shopTimezone).format('YYYY-MM-DD') === date,
            )
            .reduce((sum, order) => sum + order.summary.netSales, 0);

          dateNetSalesTotal += dateNetSales;

          return {
            id: `${teamMember.email}-${team.teamName}-unassigned-${date}`,
            name: teamMember.name,
            email: teamMember.email,
            locationId: null,
            locationName: 'Unassigned',
            dateNetSales,
            color: 'transparent',
            dates: date,
            memberTotal,
            locationTotal: memberTotal,
            teamId: team.id,
            commissionModel: `${team.commissionModel?.compensation}-${team.id}`,
            teamName: team.teamName,
            teamNetSales: teamNetSalesAndCommissionsById[team.id]?.netSales || 0,
            teamCommission: memberCommission || 0,
            totalCommission: team.commissionModel?.compensation === 'team steps'
              ? 0
              : teamNetSalesAndCommissionsById[team.id]?.commission,
          };
        });
      }

      // 6. Handle orders with valid locations
      return sortedLocations.flatMap(([locationId]) => {
        const locationOrders = filterOrdersWithLocation(aggregateTeamOrders, teamMember as TMember, locationId);

        const dates = Array.from(
          new Set(
            locationOrders.map((order) => moment.tz(order.processedAt, shop?.shopTimezone).format('YYYY-MM-DD')),
          ),
        );

        const locationTotal = locationOrders.reduce((sum, order) => sum + order.summary.netSales, 0);

        return dates.map((date) => {
          const dateNetSales = locationOrders
            .filter(
              (order) => moment.tz(order.processedAt, shop?.shopTimezone).format('YYYY-MM-DD') === date,
            )
            .reduce((sum, order) => sum + order.summary.netSales, 0);

          dateNetSalesTotal += dateNetSales;

          return {
            id: `${teamMember.email}-${team.teamName}-${locationId}-${date}`,
            name: teamMember.name,
            email: teamMember.email,
            locationId: `${String(locationId)}-${teamMember.email}` || null,
            locationName: locationNamesById[locationId]?.name || 'Unassigned',
            dateNetSales: dateNetSales || 0,
            color: locationNamesById[locationId]?.color || 'gray',
            dates: date,
            memberTotal,
            locationTotal,
            teamId: team.id,
            commissionModel: `${team.commissionModel?.compensation}-${team.id}`,
            teamName: team.teamName,
            teamNetSales: teamNetSalesAndCommissionsById[team.id]?.netSales || 0,
            teamCommission: memberCommission || 0,
            totalCommission: team.commissionModel?.compensation === 'team steps'
              ? 0
              : teamNetSalesAndCommissionsById[team.id]?.commission,
          };
        });
      });
    }));

    const summaryRow = {
      id: 'summary',
      name: null,
      teamId: '',
      teamName: 'Total',
      locationId: null,
      locationName: null,
      dateNetSales: dateNetSalesTotal,
      teamNetSales: teamNetSalesTotal,
      teamCommission: totalCommission,
      commissionModel: '',
      totalCommission,
    };

    return [...rows, summaryRow];
  }, [aggregateTeamOrders, members, commissionTeams, locationNamesById, teamNetSalesAndCommissionsById]);

  const filteredTableRows = useMemo(() => {
    const lastRow = tableData.find((row) => row.id === 'summary');
    if (filterStatus === 0) return tableData;

    const rows = tableData.filter((row) => row.teamId === filterStatus);

    return [...rows, lastRow];
  }, [filterStatus, tableData]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const tabs = useMemo(() => {
    const baseTabs = commissionTeams.map((team, index) => ({
      value: team.id,
      label: team.teamName,
      color: INDEX_TO_COLOR[index],
      count: 0,
    }));

    baseTabs.unshift({
      value: 0,
      label: t('all', { ns: 'common' }),
      color: 'info',
      count: commissionTeams.length,
    });

    return baseTabs;
  }, [commissionTeams]);

  const handleFilterStatus = useCallback((newValue: number) => {
    setFilterStatus(newValue);
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'teamId',
      headerName: t('teamName', { ns: 'inputs' }),
      width: 150,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={1.5} height="100%">
          {row.id !== 'summary' && <MemoizedBaseAvatar avatarFile={null} avatarName={row.teamName} />}
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {startCase(row.teamName)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'commissionModel',
      headerName: t('commissionModel', { ns: 'commissions' }),
      width: 150,
      renderCell: ({ row }) => {
        const commissionModel = row.commissionModel?.split('-') || [];

        return (
          <Box display="flex" alignItems="baseline" pt={1.5} height="100%">
            {row.id === 'summary' ? '' : (
              <Label
                variant="soft"
                color={
                  (commissionModel?.[0] === 'split' && 'primary')
                  || (commissionModel?.[0] === 'duplicate' && 'info')
                  || (commissionModel?.[0] === 'team steps' && 'success')
                  || 'error'
                }
              >
                {t(commissionModel?.[0], { ns: 'inputs' })}
              </Label>
            )}
          </Box>
        );
      },
    },
    {
      field: 'name',
      headerName: t('member', { ns: 'common' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={1.5} height="100%">
          {row.id !== 'summary' && <MemoizedBaseAvatar status={row.status} avatarFile={row.avatarFile} avatarName={row.name} />}
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'locationId',
      headerName: t('location', { ns: 'inputs' }),
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: row.color,
              marginRight: 1,
            }}
          />
          <Typography variant="caption">
            {row.id === 'summary' ? '' : row.locationName || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'dates',
      headerName: t('workingDates'),
      rowSpanValueGetter: () => null,
      minWidth: 100,
      renderCell: ({ row }) => (
        <Typography variant="caption">{row.id === 'summary' ? '' : row.dates || '-'}</Typography>
      ),
    },
    {
      field: 'dateNetSales',
      headerName: `${t('netSales', { ns: 'commissions' })} (${t('date', { ns: 'orders' })})`,
      width: 120,
      rowSpanValueGetter: () => null,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.id !== 'summary'
            && (
              <Typography
                {...(row.id === 'summary' && {
                  fontWeight: 600,
                })}
                variant="caption"
              >
                {getCurrency().mark}
                {' '}
                {row.dateNetSales?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            )}
        </Box>
      ),
    },
    {
      field: 'locationTotal',
      headerName: `${t('netSales', { ns: 'commissions' })} (${t('location', { ns: 'inputs' })})`,
      width: 150,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%">
          {row.id !== 'summary' && (
            <Typography
              {...(row.id === 'summary' && {
                fontWeight: 600,
              })}
              variant="caption"
            >
              {getCurrency().mark}
              {' '}
              {row.locationTotal?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'memberTotal',
      headerName: `${t('netSales', { ns: 'commissions' })} (${t('member', { ns: 'common' })})`,
      width: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%" justifyContent="flex-end">
          {row.id !== 'summary' ? (
            <Typography
              variant="caption"
            >
              {getCurrency().mark}
              {' '}
              {row.memberTotal?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          ) : (
            <Typography
              variant="caption"
            />
          )}
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: `${t('commission', { ns: 'commissions' })} (${t('member', { ns: 'common' })})`,
      width: 120,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%" justifyContent="flex-end">
          {row.id !== 'summary' ? (
            <Typography
              variant="caption"
            >
              {getCurrency().mark}
              {' '}
              {row.teamCommission?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          ) : (
            <Typography
              variant="caption"
            />
          )}
        </Box>
      ),
    },
    {
      field: 'totalCommission',
      headerName: t('teamCommission', { ns: 'commissions' }),
      minWidth: 150,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%" justifyContent="flex-end">
          <Typography
            variant="caption"
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
          >
            {getCurrency().mark}
            {' '}
            {row.totalCommission?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'teamNetSales',
      headerName: t('teamNetSales', { ns: 'commissions' }),
      flex: 1,
      minWidth: 150,
      type: 'number',
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%" justifyContent="flex-end">
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {getCurrency().mark}
            {' '}
            {row.teamNetSales?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    },
  ];

  const renderTableHeader = useCallback(() => (
    <OrdersTableHeader
      printRef={componentRef}
      handleScrollTable={handleScrollTable}
      apiRef={apiRef}
      setIsFetchByDate={setIsFetchByDate}
      isDisplayFilter={false}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => handleExport('mgm-commission-teams')}
    />
  ), [apiRef, isFullTable]);

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card sx={{
          // eslint-disable-next-line no-nested-ternary
          height: exporting ? 'auto' : (isFullTable && filteredTableRows?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto'),
          pb: 8,
          ...(isMobile && {
            height: 'auto',
            background: 'unset',
            boxShadow: 'unset',
            px: 0,
            pt: 0,
            pb: '30px',
          }),
        }}
        >
          <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />
          <DataGridPro
            apiRef={apiRef}
            localeText={localText}
            rows={filteredTableRows}
            columns={columns}
            rowSpanning
            showCellVerticalBorder
            showColumnVerticalBorder
            getRowId={(row) => row.id}
            pinnedRows={{
              bottom: [filteredTableRows[filteredTableRows.length - 1]],
            }}
            slots={{
              toolbar: renderTableHeader,
              noRowsOverlay: renderCustomNoRowsOverlay,
              footer: OrdersCountLoader as any,
            }}
            hideFooterPagination
            disableRowSelectionOnClick
            showToolbar
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export { TeamsSummaryTable };
